import { useState, useEffect, useRef } from "react";
import { ChatCircleDots, PaperPlaneRight, X, ArrowsClockwise } from "@phosphor-icons/react";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function SupportWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      apiClient.get("/support/messages").then(({ data }) => {
        setMessages(data.messages || []);
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setText("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: msg }]);
    try {
      const { data } = await apiClient.post("/support/chat", { text: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, escalated: data.escalated }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: t("supError") }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="support-widget-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center shadow-[0_0_25px_-5px_rgba(229,9,20,0.7)] transition-colors"
        title={t("supTitle")}
      >
        {open ? <X size={24} weight="bold" color="white" /> : <ChatCircleDots size={26} weight="fill" color="white" />}
      </button>

      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-24 right-6 z-40 w-[360px] max-w-[calc(100vw-3rem)] h-[480px] rounded-2xl border border-white/10 bg-[color:var(--surface)] shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-white/10 bg-[color:var(--surface-2)]">
            <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">AuraFX</p>
            <h3 className="font-display font-semibold text-base">{t("supTitle")}</h3>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-sm text-white/45 leading-relaxed p-3 rounded-xl bg-white/5" data-testid="support-welcome">
                {t("supWelcome")}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[color:var(--accent)] text-white"
                      : m.role === "admin"
                      ? "bg-emerald-500/15 border border-emerald-500/30 text-white/90"
                      : "bg-white/5 text-white/85"
                  }`}
                >
                  {m.role === "admin" && (
                    <p className="text-[10px] font-mono-num uppercase tracking-widest text-emerald-300 mb-1">{t("supTeam")}</p>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 rounded-xl px-3.5 py-2.5">
                  <ArrowsClockwise size={16} className="animate-spin text-white/50" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              data-testid="support-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              maxLength={1500}
              placeholder={t("supPlaceholder")}
              className="flex-1 bg-[color:var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-white/25"
            />
            <button
              data-testid="support-send-btn"
              onClick={send}
              disabled={sending || !text.trim()}
              className="w-11 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center disabled:opacity-40 transition-colors"
            >
              <PaperPlaneRight size={18} weight="fill" color="white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
